<template>
    <div class="support-ticket-create">
        <div class="create-header">
            <div class="header-title">CREATE NEW TICKET</div>
            <button class="button-back" @click="goBack">← BACK</button>
        </div>
        
        <div class="create-content">
            <div class="form-group">
                <label class="form-label">SUBJECT</label>
                <input 
                    v-model="formData.subject" 
                    type="text" 
                    class="form-input" 
                    placeholder="Enter ticket subject..."
                    maxlength="200"
                />
            </div>
            
            <div class="form-group">
                <label class="form-label">PRIORITY</label>
                <select v-model="formData.priority" class="form-select">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                </select>
            </div>
            
            <div class="form-group">
                <label class="form-label">MESSAGE</label>
                <textarea 
                    v-model="formData.message" 
                    class="form-textarea" 
                    placeholder="Describe your issue or question..."
                    rows="6"
                    maxlength="5000"
                ></textarea>
                <div class="form-char-count">{{ formData.message.length }} / 5000</div>
            </div>
            
            <div v-if="openTicketsCount >= 5" class="form-warning">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 3V9M8 13H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                Maximum 5 open tickets allowed. Please close some tickets before creating new ones.
            </div>
        </div>
        
        <div class="create-footer">
            <button 
                class="button-submit" 
                @click="submitTicket" 
                :disabled="!canSubmit || socketSendLoading !== null"
            >
                <span v-if="socketSendLoading === 'SupportCreateTicket'">CREATING...</span>
                <span v-else>CREATE TICKET</span>
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
    name: 'SupportTicketCreate',
    data() {
        return {
            formData: {
                subject: '',
                priority: 'medium',
                message: ''
            }
        }
    },
    methods: {
        ...mapActions([
            'supportSetView',
            'supportCreateTicketSocket',
            'notificationShow'
        ]),
        goBack() {
            this.supportSetView('list');
        },
        submitTicket() {
            if(!this.canSubmit) { return; }
            
            if(this.formData.subject.trim() === '') {
                this.notificationShow({ type: 'error', message: 'Please enter a subject.' });
                return;
            }
            
            if(this.formData.message.trim() === '') {
                this.notificationShow({ type: 'error', message: 'Please enter a message.' });
                return;
            }
            
            this.supportCreateTicketSocket({
                subject: this.formData.subject.trim(),
                priority: this.formData.priority,
                message: this.formData.message.trim()
            });
        }
    },
    computed: {
        ...mapGetters([
            'supportTickets',
            'socketSendLoading'
        ]),
        openTicketsCount() {
            if(!this.supportTickets.data) { return 0; }
            return this.supportTickets.data.filter(t => t.status === 'open' || t.status === 'in-progress').length;
        },
        canSubmit() {
            return this.formData.subject.trim() !== '' && 
                   this.formData.message.trim() !== '' && 
                   this.openTicketsCount < 5;
        }
    }
}
</script>

<style scoped>
.support-ticket-create {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.create-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-title {
    font-size: 16px;
    font-weight: 800;
    color: #ffffff;
}

.button-back {
    padding: 6px 12px;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.button-back:hover {
    border-color: var(--accent-yellow);
    color: var(--accent-yellow);
}

.create-content {
    flex: 1;
    overflow-y: auto;
    padding: 15px;
}

.form-group {
    margin-bottom: 20px;
}

.form-label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #80889A;
    margin-bottom: 8px;
    text-transform: uppercase;
}

.form-input,
.form-select,
.form-textarea {
    width: 100%;
    padding: 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: #ffffff;
    font-size: 14px;
    font-family: inherit;
    outline: none;
    transition: all 0.3s ease;
    box-sizing: border-box;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
    background: rgba(255, 255, 255, 0.08);
    border-color: var(--accent-yellow);
}

.form-input::placeholder,
.form-textarea::placeholder {
    color: #5e768e;
}

.form-select {
    cursor: pointer;
}

.form-textarea {
    resize: vertical;
    min-height: 120px;
    font-family: inherit;
}

.form-char-count {
    text-align: right;
    font-size: 11px;
    color: #80889A;
    margin-top: 5px;
}

.form-warning {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: rgba(255, 204, 0, 0.1);
    border: 1px solid rgba(255, 204, 0, 0.3);
    border-radius: 8px;
    color: #ffcc00;
    font-size: 12px;
}

.form-warning svg {
    flex-shrink: 0;
}

.create-footer {
    padding: 15px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.button-submit {
    width: 100%;
    padding: 12px;
    background: var(--gradient-button-bg);
    border: none;
    border-radius: 8px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
}

.button-submit:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(1, 225, 164, 0.3);
}

.button-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>

